/**
 * Escala de maturidade 0–4 + NA para a UI: rubrica ("o que observar"), cor do
 * nível e o rótulo do motor, num lugar só.
 *
 * Existe porque a rubrica estava copiada palavra por palavra no Checklist mobile
 * e no desktop, e a legenda da impressão tinha divergido da fonte canônica
 * ("Incipiente" onde o motor diz "Estado Inicial"). Rótulo de nível é regra da
 * metodologia e vem SEMPRE de `engine.situacao` — aqui mora só o texto de UI.
 *
 * Fora daqui de propósito: a rubrica da Supervisão AUDIN
 * (`components/SupervisaoChecklist.tsx`), que descreve os mesmos níveis pela
 * ótica da 3ª linha ("critério não realizado…"). Mesma escala, outra leitura —
 * unificar os textos apagaria a distinção.
 */
import type { NivelMaturidade } from "@caint/engine";
import { engine } from "./engine.ts";

/** Os níveis na ordem de exibição (0→4, NA por último). */
export const NIVEIS_MATURIDADE: readonly NivelMaturidade[] = ["0", "1", "2", "3", "4", "na"];

/** O que se observa em campo para cada nível (rubrica ancorada do avaliador). */
export const RUBRICA_MATURIDADE: Readonly<Record<NivelMaturidade, string>> = {
  "0": "Não há POP, rotina nem registros.",
  "1": "Existe informalmente, sem padronização — depende do plantão.",
  "2": "POP existe e está datado; aplicação parcial.",
  "3": "Implementado, padronizado e seguido; equipe treinada.",
  "4": "Supera o requisito; monitorado por indicadores, com melhoria.",
  na: "Não se aplica ao perfil da unidade; excluído do cálculo.",
};

/** Cor de cada nível (tokens do design system). */
export const COR_MATURIDADE: Readonly<Record<NivelMaturidade, string>> = {
  "0": "var(--mat-0)",
  "1": "var(--mat-1)",
  "2": "var(--mat-2)",
  "3": "var(--mat-3)",
  "4": "var(--mat-4)",
  na: "var(--mat-na)",
};

/**
 * Nível válido, ou `null`. Porta de entrada obrigatória para qualquer valor que
 * venha do banco (ex.: `PlanoItem.maturidadeGatilho`, que é `string | null`):
 * `Maturidade.de` LANÇA em valor inválido, e uma exceção dessas dentro do render
 * derruba a árvore React inteira da tela.
 */
export function nivelValido(v: string | null | undefined): NivelMaturidade | null {
  return v != null && (NIVEIS_MATURIDADE as readonly string[]).includes(v) ? (v as NivelMaturidade) : null;
}

/** Como o nível é escrito num selo: "0".."4" e "NA". */
export function rotuloNivel(n: NivelMaturidade): string {
  return n === "na" ? "NA" : n;
}

/** Uma linha da escala, pronta para renderizar. */
export interface DescricaoNivel {
  readonly nivel: NivelMaturidade;
  readonly rotulo: string;
  readonly situacao: string; // do motor: Ausente, Estado Inicial, …
  readonly rubrica: string;
  readonly cor: string;
}

export function descricaoNivel(n: NivelMaturidade): DescricaoNivel {
  return {
    nivel: n,
    rotulo: rotuloNivel(n),
    situacao: engine.situacao(n),
    rubrica: RUBRICA_MATURIDADE[n],
    cor: COR_MATURIDADE[n],
  };
}

/** A escala inteira, na ordem — alimenta as legendas. */
export function escalaCompleta(): readonly DescricaoNivel[] {
  return NIVEIS_MATURIDADE.map(descricaoNivel);
}
